/**
 * Zone lighting rigs — the art direction as light, rather than as pigment.
 *
 * `palette.ts` says what colour a zone is. This says where the light in it comes
 * from, and it is the other half of the same decision: the fattening hall's
 * "endless amber sodium haze" is a fog colour AND a row of fixtures too far
 * apart, and neither half reads without the other.
 *
 * ── The one rule ──────────────────────────────────────────────────────────────
 * **Fixtures brighten a room; they never move where the bands fall.** The toon
 * ramp is anchored to `setExposureReference()`, computed here from the key and
 * ambient terms only. Add a lamp and you get a pool of light; you do not
 * silently push every surface in the room up a band and change the zone's value
 * structure, which is the thing the whole palette is built on.
 *
 * ── Why one shadow-casting directional in an interior ─────────────────────────
 * A farrowing hall is really lit by a row of battens, and the honest way to do
 * that is a shadow-casting light per batten. That is six shadow maps, and on the
 * integrated GPU this game targets (spec §1) it is the whole frame budget for an
 * effect nobody would name. So: one shadow-casting directional, angled and
 * coloured like the fixtures, carries the crate shadows that make the room
 * legible; the fixtures themselves are cheap unshadowed pools that put light
 * where the eye expects it. Standard practice, and it is a deliberate lie —
 * written down here so nobody later "fixes" it.
 */

import * as THREE from 'three';
import { applyZone, setExposureReference } from '@/render/toon';
import type { ZonePalette } from '@/render/palette';

export interface Vec3Like {
  x: number;
  y: number;
  z: number;
}

/**
 * A practical light in the room — something the player can see the fitting of.
 *
 * `batten` is a fluorescent tube. It is not a point: a 1.5 m tube lights an
 * ellipse, and approximating it with one point light puts a circular hotspot
 * under the middle of a fitting whose whole visual character is that it is long.
 *
 * `spot` is a fitting with a SHADE on it — a desk lamp, and beat 11's office is
 * the first room that is one practical. The distinction that matters is not
 * softness, it is that **a point light lights a face from below as readily as
 * from above**, and D-187's whole anti-villain lever is a lamp under Kestner's
 * eyeline that lights the page and his hands while the top of his head stays
 * dark. A point light cannot do that at any intensity, because the only aiming
 * it has is where it is. See `officeFixtures()` for the two frames that cost.
 *
 * **A spot costs exactly what the point it replaces cost**, which is why this
 * was affordable: D-027 found this hardware is bound by a per-fragment loop over
 * lights (15 lights 30 fps, 5 lights 60 fps), and a spot is one light in that
 * loop just as a point is. It casts no shadow, for the same reason nothing here
 * but the key does.
 */
export interface Fixture {
  kind: 'point' | 'batten' | 'spot';
  position: Vec3Like;
  /** Length in metres, along X. `batten` only. */
  length?: number;
  /**
   * Where a `spot` points. **Required for `spot`** — `createZoneLighting`
   * throws without it rather than defaulting, because the default a spot would
   * want is "straight down" and three's own default is "the world origin",
   * which for a desk lamp is a light aimed across the room at knee height.
   *
   * It is a world point and not a direction on purpose: every other number in a
   * fixture is a place, and the thing an author actually knows about a desk lamp
   * is what it is pointed AT.
   */
  target?: Vec3Like;
  /** Cone half-angle in radians. `spot` only; defaults to 0.6 (~34°). */
  angle?: number;
  /** 0..1 softness of the cone edge. `spot` only; defaults to 0.5. */
  penumbra?: number;
  /** Defaults to the zone key. The cull corridor's one red lamp overrides it. */
  colour?: number;
  intensity: number;
  /** Cutoff distance in metres. */
  range: number;
  /**
   * A name on the resulting light, so a director can reach one fixture again.
   *
   * Almost nothing wants this and almost nothing should: a room's lighting is a
   * design, not a set of switches, and a beat that dims a lamp because it needs
   * the player to look somewhere has to justify itself. Beat 3 is the first case
   * that does (D-087) — the warm lamp over the empty straw pen is the only thing
   * in the fattening hall that can say *this pen, now* without a line of text.
   *
   * A `batten` is several point lights; each segment gets `${name}-${index}`, so
   * `getObjectByName(name)` on a batten deliberately finds nothing rather than
   * silently handing back one seventh of a fitting.
   */
  name?: string;
}

export interface ZoneLightingOptions {
  fixtures?: readonly Fixture[];
  /**
   * Where the key comes FROM. Kept low by default: a high sun gives short
   * shadows, and the shadow is how the eye reads what cast it.
   */
  keyDirection?: Vec3Like;
  keyIntensity?: number;
  /** Half-extent of the shadow camera in metres. Cover the play space, no more. */
  shadowExtent?: number;
  /** Sky/ground fill. This is the warm bounce, and it is never neutral. */
  hemiIntensity?: number;
  /** Flat floor of light, so nothing is ever pure black. */
  ambientIntensity?: number;
  /**
   * The irradiance that counts as fully lit — the top of the toon ramp.
   *
   * Defaults to key + hemisphere + ambient, which is right for a room lit from
   * outside and **badly wrong for one lit by fixtures**: the practicals then
   * push every surface far past the anchor, everything clamps to the top band,
   * and the room renders as one flat tone. That is not a subtle failure, it is
   * the whole shader appearing not to work, and the farrowing hall hit it on the
   * first build. If a room has practicals, state this explicitly and include
   * roughly what they deliver to a lit floor.
   */
  exposureReference?: number;
  /**
   * Fog density multiplier over the zone's own `haze`. The hall is hazed and
   * outside is clear; this scales that without relitigating the palette.
   */
  fogScale?: number;
}

export interface ZoneRig {
  /** Add this to the scene. */
  readonly group: THREE.Group;
  /**
   * Assign to `scene.background`.
   *
   * There is deliberately no `fog` here to assign to `scene.fog`. The haze is a
   * uniform on the toon material instead, set by this function — see the long
   * note in `src/render/toon.ts` about three uploading its fog colour in a
   * colour space that depends on the render target.
   */
  readonly background: THREE.Color;
  /** The shadow-casting key, exposed so a room can aim it at its own centre. */
  readonly key: THREE.DirectionalLight;
  /**
   * The sky term, exposed so a room with **weather** can drive its irradiance.
   *
   * Beat 6 and nothing else, and it is D-107's second reader: `storm.flash`
   * raises this so surfaces genuinely climb the toon ramp during a strike. A
   * pure post-process white would flatten the silhouettes the flash exists to
   * reveal, which is the whole reason that decision names three readers rather
   * than one. The hemisphere is the right one of the three lights to drive —
   * lightning is a thing the *sky* does, and this is the sky.
   *
   * It is not a fifth point light and the budget gate's count is unmoved: the
   * flash still never adds a fixture (`docs/beat-6-plan.md` rule 1).
   */
  readonly hemi: THREE.HemisphereLight;
  dispose(): void;
}

const DEFAULTS = {
  keyDirection: { x: 0.35, y: 0.82, z: 0.45 },
  keyIntensity: 0.62,
  shadowExtent: 14,
  hemiIntensity: 0.3,
  ambientIntensity: 0.1,
  fogScale: 1,
} as const;

/**
 * Haze 0..1 from the palette → exponential fog density.
 *
 * Exponential rather than linear because these spaces are meant to feel
 * endless: linear fog has a far plane, and everything at that distance goes flat
 * at once in a visible band.
 *
 * The multiplier was 0.055 on the first build and it was far too weak — the
 * farrowing hall's far wall, 18 m away, came back at 2 % fog and read as a wall
 * rather than as a hall going on. The factor is `1 - exp(-(density·z)²)`, so
 * density has to be around 1/z at the distance you want half dissolved.
 *
 * It then moved again, from 0.30 to 0.45, once the haze became a linear-space
 * mix against a correctly-linearised fog colour. Before that it was being mixed
 * against an already-encoded colour, which made it look about two and a half
 * times stronger than it was; the number that "looked right" was measuring a
 * bug. **A fog factor of 1 now really does land on the palette's `fog` value**,
 * so the density is what decides how far away that happens, and 0.45 puts the
 * farrowing hall's far wall at roughly three quarters dissolved.
 *
 * Because the curve is quadratic it bites hard at the high end: an outdoor zone
 * should pass a `fogScale` well below 1 rather than have its `haze` lowered,
 * since `haze` is a palette value and describes the AIR, not the draw distance.
 * `bloom.ts` is its second reader, so zone transitions retain this same mapping.
 */
export function fogDensity(haze: number, scale: number): number {
  return haze * 0.45 * scale;
}

/**
 * How many point lights a batten becomes.
 *
 * **One.** It was three, on the reasoning that a 1.5 m tube lights an ellipse and
 * a single point light puts a round hotspot under a long fitting. That reasoning
 * is correct and it was written before anything was measured, which turned out
 * to be the whole problem.
 *
 * Measured, at 1920x1080 on this box's Vega 3 — the machine spec §1 calls the
 * target — with the dressed farrowing hall and its five battens:
 *
 * | point lights | frame |
 * |---|---|
 * | 15 (3 per batten) | 33.3 ms — **30 fps** |
 * | 10 (2 per batten) | 33.3 ms — **30 fps** with the post stack |
 * |  5 (1 per batten) | 16.7 ms — **60 fps**, locked |
 *
 * Nothing else moved: same 121 k triangles, same 183 draw calls, same post
 * stack. The dominant cost of this room was a per-fragment loop over point
 * lights, and that is not what spec §1's "≤ 400 draw calls" budget makes you
 * look for. See D-027.
 *
 * **It did not finish the job.** The hall now hits 60 fps on about half its
 * frames instead of none of them, and still misses the budget — see the
 * expected-to-fail test at the bottom of `tests/e2e/perf.spec.ts` for the full
 * numbers and for what else has already been ruled out.
 *
 * The round hotspot is real and the ramp bands most of it away. If a zone ever
 * genuinely needs the elongated pool, spend the lights there and take them back
 * somewhere else — do not raise this globally.
 */
const BATTEN_SEGMENTS: number = 1;

export function createZoneLighting(
  zone: ZonePalette,
  options: ZoneLightingOptions = {},
): ZoneRig {
  const keyDirection = options.keyDirection ?? DEFAULTS.keyDirection;
  const keyIntensity = options.keyIntensity ?? DEFAULTS.keyIntensity;
  const shadowExtent = options.shadowExtent ?? DEFAULTS.shadowExtent;
  const hemiIntensity = options.hemiIntensity ?? DEFAULTS.hemiIntensity;
  const ambientIntensity = options.ambientIntensity ?? DEFAULTS.ambientIntensity;
  const fogScale = options.fogScale ?? DEFAULTS.fogScale;

  const group = new THREE.Group();
  group.name = `lighting-${zone.id}`;

  const ambient = new THREE.AmbientLight(zone.shadow, ambientIntensity);
  group.add(ambient);

  // Sky colour is the zone KEY and ground colour is the zone FILL, not white
  // and grey. This is where "shadows tint toward the zone's fill hue" actually
  // comes from — the ramp's shadow band sets the colour, and this makes the
  // irradiance that lands there agree with it instead of fighting it.
  const hemi = new THREE.HemisphereLight(zone.key, zone.fill, hemiIntensity);
  group.add(hemi);

  const key = new THREE.DirectionalLight(zone.key, keyIntensity);
  key.position.set(keyDirection.x, keyDirection.y, keyDirection.z).normalize().multiplyScalar(18);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.camera.near = 0.5;
  key.shadow.camera.far = 60;
  key.shadow.camera.left = -shadowExtent;
  key.shadow.camera.right = shadowExtent;
  key.shadow.camera.top = shadowExtent;
  key.shadow.camera.bottom = -shadowExtent;
  // Three does not refresh the shadow camera's projection when these bounds are
  // assigned, so without this the frustum stays at its ±5 default and anything
  // further out casts no shadow at all — which reads as a lighting bug in the
  // far half of the room and sends you looking in entirely the wrong place.
  key.shadow.camera.updateProjectionMatrix();
  // Normal bias rather than depth bias. Depth bias at this map resolution needs
  // to be large enough to detach contact shadows from what casts them, and a
  // crate that floats is worse than a little acne.
  key.shadow.normalBias = 0.02;
  key.shadow.bias = -0.0004;
  group.add(key);
  group.add(key.target);

  for (const fixture of options.fixtures ?? []) {
    const colour = fixture.colour ?? zone.key;
    if (fixture.kind === 'point') {
      const light = new THREE.PointLight(colour, fixture.intensity, fixture.range, 1.6);
      light.position.set(fixture.position.x, fixture.position.y, fixture.position.z);
      if (fixture.name !== undefined) light.name = fixture.name;
      group.add(light);
      continue;
    }

    if (fixture.kind === 'spot') {
      if (fixture.target === undefined) {
        throw new Error(`fixture "${fixture.name ?? '(unnamed)'}": a spot must declare a target`);
      }
      const light = new THREE.SpotLight(
        colour,
        fixture.intensity,
        fixture.range,
        fixture.angle ?? 0.6,
        fixture.penumbra ?? 0.5,
        // The same softer-than-physical decay the other two use, and for the
        // same reason — see the batten's note below.
        1.6,
      );
      light.position.set(fixture.position.x, fixture.position.y, fixture.position.z);
      // Three reads the target's WORLD position, so the target has to be in the
      // graph. A `SpotLight` whose target was only assigned and never added is
      // a light aimed at the world origin, and it fails silently: the room is
      // lit, just not by the thing you aimed.
      light.target.position.set(fixture.target.x, fixture.target.y, fixture.target.z);
      group.add(light.target);
      if (fixture.name !== undefined) light.name = fixture.name;
      group.add(light);
      continue;
    }

    const length = fixture.length ?? 1.5;
    for (let i = 0; i < BATTEN_SEGMENTS; i++) {
      const s = BATTEN_SEGMENTS === 1 ? 0.5 : i / (BATTEN_SEGMENTS - 1);
      const light = new THREE.PointLight(
        colour,
        fixture.intensity / BATTEN_SEGMENTS,
        fixture.range,
        // Softer than physical (2.0) on purpose. Inverse-square in a small
        // interior puts almost all the light in the top half metre under the
        // fitting and leaves the floor black, which is dramatic and completely
        // unlike a lit farm building.
        1.6,
      );
      light.position.set(
        fixture.position.x + (s - 0.5) * length,
        fixture.position.y,
        fixture.position.z,
      );
      if (fixture.name !== undefined) light.name = `${fixture.name}-${String(i)}`;
      group.add(light);
    }
  }

  applyZone(zone, fogDensity(zone.haze, fogScale));
  setExposureReference(
    options.exposureReference ?? keyIntensity + hemiIntensity + ambientIntensity,
  );

  const background = new THREE.Color(zone.fog);

  return {
    group,
    background,
    key,
    hemi,
    dispose(): void {
      key.shadow.dispose();
      group.clear();
    },
  };
}
